import { Col, Spinner2, Text } from 'components';
import { Api, formatCurrencyAmount } from 'lib';
import { useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { COUNTRY_OPTIONS, formatPercent, getDefaultDateRange } from './utils'

const PAGE_SIZE_OPTIONS = [10, 25, 50, 100];

function getPageNumbers(page, totalPages) {
  const WINDOW = 2;
  const pages = [];
  const start = Math.max(1, page - WINDOW);
  const end = Math.min(totalPages, page + WINDOW);
  if (start > 1) {
    pages.push(1);
    if (start > 2) pages.push('...');
  }
  for (let i = start; i <= end; i++) pages.push(i);
  if (end < totalPages) {
    if (end < totalPages - 1) pages.push('...');
    pages.push(totalPages);
  }
  return pages;
}

const buildAuditQueryString = ({
  startDate,
  endDate,
  countries,
  teamId,
  interval,
  status,
  search,
  page,
  pageSize
}) => {
  const params = new URLSearchParams();

  if (startDate) params.set('startDate', `${startDate}T00:00:00.000Z`);
  if (endDate) params.set('endDate', `${endDate}T23:59:59.999Z`);
  if (countries && countries.length) params.set('countryCodes', countries.join(','));
  if (teamId) params.set('teamIds', teamId);
  if (interval) params.set('interval', interval);
  if (status) params.set('status', status);
  if (search) params.set('search', search);
  if (page) params.set('page', String(page));
  if (pageSize) params.set('pageSize', String(pageSize));

  return params.toString();
};

const normalizeManualTransferTotals = (totals) => {
  if (Array.isArray(totals)) return totals;
  if (!totals || typeof totals !== 'object') return [];

  return Object.entries(totals).map(([currency, values]) => ({
    currency,
    totalDue: Number(values?.totalDue || 0),
    totalReconciled: Number(values?.totalReconciled || 0)
  }));
};



const ManualTransferStatusBadge = ({ status, t }) => {
  if (status === 'reconciled') {
    return (
      <span className="inline-flex items-center gap-1 text-[11px] font-semibold text-emerald-700 bg-emerald-50 border border-emerald-200 rounded-full px-2 py-0.5">
        <span className="w-1.5 h-1.5 rounded-full bg-emerald-500" />
        {t('subscriptions.admin.revenueSplit.manualTransferAudit.status.reconciled', {
          defaultValue: 'Reconciled'
        })}
      </span>
    );
  }
  if (status === 'not_required') {
    return (
      <span className="inline-flex items-center gap-1 text-[11px] font-semibold text-slate-500 bg-slate-50 border border-slate-200 rounded-full px-2 py-0.5">
        {t('subscriptions.admin.revenueSplit.manualTransferAudit.status.not_required', {
          defaultValue: 'Not Required'
        })}
      </span>
    );
  }
  return (
    <span className="inline-flex items-center gap-1 text-[11px] font-semibold text-amber-700 bg-amber-50 border border-amber-200 rounded-full px-2 py-0.5">
      <span className="w-1.5 h-1.5 rounded-full bg-amber-500" />
      {t('subscriptions.admin.revenueSplit.manualTransferAudit.status.pending', {
        defaultValue: 'Pending'
      })}
    </span>
  );
};

const SkipReasonBadge = ({ reason, t }) => {
  if (!reason) return null;
  const labelKey = `subscriptions.admin.revenueSplit.manualTransferAudit.skipReason.${reason}`;
  const defaultLabels = {
    no_profit_split: 'No Profit Split',
    no_connected_account: 'No Connected Account',
    cross_region: 'Cross Region'
  };
  return (
    <span className="inline-flex items-center text-[11px] font-medium text-slate-600 bg-slate-100 border border-slate-200 rounded px-2 py-0.5">
      {t(labelKey, { defaultValue: defaultLabels[reason] || reason })}
    </span>
  );
};

const ManualAuditTab = () => {
  const { t } = useTranslation();
  const defaults = useMemo(() => getDefaultDateRange(), []);

  const [startDate, setStartDate] = useState(defaults.startDate);
  const [endDate, setEndDate] = useState(defaults.endDate);
  const [selectedCountries, setSelectedCountries] = useState([]);
  const [selectedTeamId, setSelectedTeamId] = useState('');
  const [teams, setTeams] = useState([]);
  const [loadingTeams, setLoadingTeams] = useState(false);
  const [showCountryFilter, setShowCountryFilter] = useState(false);
  const [interval, setInterval] = useState('');
  const [status, setStatus] = useState('');
  const [search, setSearch] = useState('');
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(50);
  const countryFilterRef = useRef(null);

  const [selectedEventIds, setSelectedEventIds] = useState(new Set());
  const [bulkReconcileOpen, setBulkReconcileOpen] = useState(false);
  const [bulkReconcileNote, setBulkReconcileNote] = useState('');
  const [bulkReconciling, setBulkReconciling] = useState(false);

  const [loading, setLoading] = useState(true);
  const [downloading, setDownloading] = useState(false);
  const [rows, setRows] = useState([]);
  const [totals, setTotals] = useState([]);
  const [total, setTotal] = useState(0);
  const [error, setError] = useState(false);

  const [reconcileModal, setReconcileModal] = useState(null); // { row, targetStatus }
  const [reconcileNote, setReconcileNote] = useState('');
  const [reconciling, setReconciling] = useState(false);

  useEffect(() => {
    if (!showCountryFilter) return;
    function handleOutsideClick(e) {
      if (countryFilterRef.current && !countryFilterRef.current.contains(e.target)) {
        setShowCountryFilter(false);
      }
    }
    document.addEventListener('mousedown', handleOutsideClick);
    return () => document.removeEventListener('mousedown', handleOutsideClick);
  }, [showCountryFilter]);

  useEffect(() => {
    fetchTeamsByCountries();
  }, [selectedCountries.join(',')]);

  useEffect(() => {
    setPage(1);
    setSelectedEventIds(new Set());
  }, [startDate, endDate, selectedCountries.join(','), selectedTeamId, interval, status, search, pageSize]);

  useEffect(() => {
    setSelectedEventIds(new Set());
    fetchAuditRows();
  }, [
    startDate,
    endDate,
    selectedCountries.join(','),
    selectedTeamId,
    interval,
    status,
    search,
    page,
    pageSize
  ]);

  async function fetchTeamsByCountries() {
    setSelectedTeamId('');
    if (!selectedCountries.length) {
      setTeams([]);
      return;
    }
    setLoadingTeams(true);
    try {
      const responses = await Promise.all(
        selectedCountries.map((countryCode) =>
          Api.get(`/admin/getTeams?countryCode=${countryCode}`)
        )
      );
      const deduped = new Map();
      responses.forEach((res) => {
        if (!Array.isArray(res)) return;
        res.forEach((team) => {
          if (!team?._id) return;
          deduped.set(team._id, team);
        });
      });
      setTeams(
        Array.from(deduped.values()).sort((a, b) => (a.name || '').localeCompare(b.name || ''))
      );
    } finally {
      setLoadingTeams(false);
    }
  }

  async function fetchAuditRows() {
    setLoading(true);
    setError(false);

    const query = buildAuditQueryString({
      startDate,
      endDate,
      countries: selectedCountries,
      teamId: selectedTeamId,
      interval,
      status,
      search,
      page,
      pageSize
    });

    try {
      const res = await Api.get(`/admin/subscriptionPlans/manualTransferAudit?${query}`);
      if (res?.data) {
        setRows(res.data.rows || []);
        setTotals(normalizeManualTransferTotals(res.data.totals));
        setTotal(res.data.total || 0);
      } else {
        setRows([]);
        setTotals([]);
        setTotal(0);
        setError(true);
      }
    } catch {
      setRows([]);
      setTotals([]);
      setTotal(0);
      setError(true);
    } finally {
      setLoading(false);
    }
  }

  async function downloadAuditCsv() {
    setDownloading(true);
    const query = buildAuditQueryString({
      startDate,
      endDate,
      countries: selectedCountries,
      teamId: selectedTeamId,
      interval,
      status,
      search
    });
    try {
      const blob = await Api.getBlob(`/admin/subscriptionPlans/manualTransferAudit/csv?${query}`);
      if (!blob || !(blob instanceof Blob)) return;
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `manual-transfer-audit-${startDate}-${endDate}.csv`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);
    } finally {
      setDownloading(false);
    }
  }

  function openReconcileModal(row) {
    setReconcileNote('');
    setReconcileModal({ row, targetStatus: 'reconciled' });
  }

  async function submitReconcile() {
    if (!reconcileModal) return;
    setReconciling(true);
    try {
      await Api.post('/admin/subscriptionPlans/manualTransferAudit/reconcile', {
        eventId: reconcileModal.row.eventId,
        status: reconcileModal.targetStatus,
        note: reconcileNote || undefined
      });
      setReconcileModal(null);
      fetchAuditRows();
    } finally {
      setReconciling(false);
    }
  }

  async function submitBulkReconcile() {
    setBulkReconciling(true);
    try {
      await Promise.all(
        [...selectedEventIds].map((eventId) =>
          Api.post('/admin/subscriptionPlans/manualTransferAudit/reconcile', {
            eventId,
            status: 'reconciled',
            note: bulkReconcileNote || undefined
          })
        )
      );
      setBulkReconcileOpen(false);
      setBulkReconcileNote('');
      setSelectedEventIds(new Set());
      fetchAuditRows();
    } finally {
      setBulkReconciling(false);
    }
  }

  const totalPages = Math.ceil(total / pageSize);
  const pendingRows = rows.filter((r) => r.manualTransferStatus === 'pending');
  const allPendingSelected =
    pendingRows.length > 0 && pendingRows.every((r) => selectedEventIds.has(r.eventId));
  const somePendingSelected =
    pendingRows.some((r) => selectedEventIds.has(r.eventId)) && !allPendingSelected;

  return (
    <div className="h-full flex flex-col bg-[#F8FAFC]">
      {/* Filters */}
      <div className="relative z-50 border-b border-[#E2E8F0] bg-white px-6 py-4 shrink-0">
        <div className="grid grid-cols-2 md:grid-cols-4 xl:grid-cols-[repeat(6,minmax(0,1fr))_auto] gap-3 items-end">
          <label className="flex flex-col gap-1">
            <span className="text-[11px] font-semibold uppercase tracking-wide text-[#64748B]">
              {t('subscriptions.admin.revenueSplit.filters.startDate', {
                defaultValue: 'Start date'
              })}
            </span>
            <input
              type="date"
              className="h-10 rounded-lg border border-[#CBD5E1] px-3 text-[13px] text-[#334155] bg-white"
              value={startDate}
              onChange={(e) => setStartDate(e.target.value)}
            />
          </label>

          <label className="flex flex-col gap-1">
            <span className="text-[11px] font-semibold uppercase tracking-wide text-[#64748B]">
              {t('subscriptions.admin.revenueSplit.filters.endDate', { defaultValue: 'End date' })}
            </span>
            <input
              type="date"
              className="h-10 rounded-lg border border-[#CBD5E1] px-3 text-[13px] text-[#334155] bg-white"
              value={endDate}
              onChange={(e) => setEndDate(e.target.value)}
            />
          </label>

          <div className="relative" ref={countryFilterRef}>
            <label className="flex flex-col gap-1">
              <span className="text-[11px] font-semibold uppercase tracking-wide text-[#64748B]">
                {t('subscriptions.admin.revenueSplit.filters.countries', {
                  defaultValue: 'Countries'
                })}
              </span>
              <button
                type="button"
                className="h-10 rounded-lg px-3 text-left text-[13px] text-[#334155] bg-white"
                style={{ border: '1px solid #CBD5E1' }}
                onClick={() => setShowCountryFilter((prev) => !prev)}
              >
                {selectedCountries.length
                  ? t('subscriptions.admin.revenueSplit.filters.selectedCountries', {
                    defaultValue: '{{count}} selected',
                    count: selectedCountries.length
                  })
                  : t('subscriptions.admin.revenueSplit.filters.selectCountries', {
                    defaultValue: 'Select countries'
                  })}
              </button>
            </label>
            {showCountryFilter && (
              <div className="absolute z-30 mt-1 w-full max-h-56 overflow-auto rounded-lg border border-[#CBD5E1] bg-white shadow-lg p-2 space-y-1">
                {COUNTRY_OPTIONS.map((country) => {
                  const checked = selectedCountries.includes(country.code);
                  return (
                    <label
                      key={country.code}
                      className="flex items-center gap-2 px-2 py-1 rounded hover:bg-[#F1F5F9] cursor-pointer"
                    >
                      <input
                        type="checkbox"
                        checked={checked}
                        onChange={(e) => {
                          if (e.target.checked) {
                            setSelectedCountries((prev) => [...prev, country.code]);
                          } else {
                            setSelectedCountries((prev) =>
                              prev.filter((item) => item !== country.code)
                            );
                          }
                        }}
                      />
                      <span className="text-[13px] text-[#334155]">{t(country.label)}</span>
                    </label>
                  );
                })}
              </div>
            )}
          </div>

          <label className="flex flex-col gap-1">
            <span className="text-[11px] font-semibold uppercase tracking-wide text-[#64748B]">
              {t('subscriptions.admin.revenueSplit.filters.team', { defaultValue: 'Team' })}
            </span>
            <select
              className="h-10 rounded-lg border border-[#CBD5E1] px-3 text-[13px] text-[#334155] bg-white disabled:bg-[#F8FAFC]"
              value={selectedTeamId}
              onChange={(e) => setSelectedTeamId(e.target.value)}
              disabled={!selectedCountries.length || loadingTeams}
            >
              <option value="">
                {loadingTeams
                  ? t('subscriptions.admin.revenueSplit.filters.loadingTeams', {
                    defaultValue: 'Loading teams...'
                  })
                  : t('subscriptions.admin.revenueSplit.filters.allTeams', {
                    defaultValue: 'All teams'
                  })}
              </option>
              {teams.map((team) => (
                <option key={team._id} value={team._id}>
                  {team.name}
                </option>
              ))}
            </select>
          </label>

          <label className="flex flex-col gap-1">
            <span className="text-[11px] font-semibold uppercase tracking-wide text-[#64748B]">
              {t('subscriptions.admin.revenueSplit.filters.interval', { defaultValue: 'Interval' })}
            </span>
            <select
              className="h-10 rounded-lg border border-[#CBD5E1] px-3 text-[13px] text-[#334155] bg-white"
              value={interval}
              onChange={(e) => setInterval(e.target.value)}
            >
              <option value="">
                {t('subscriptions.admin.revenueSplit.filters.all', { defaultValue: 'All' })}
              </option>
              <option value="monthly">
                {t('subscriptions.admin.revenueSplit.filters.monthly', { defaultValue: 'Monthly' })}
              </option>
              <option value="yearly">
                {t('subscriptions.admin.revenueSplit.filters.yearly', { defaultValue: 'Yearly' })}
              </option>
            </select>
          </label>

          <label className="flex flex-col gap-1">
            <span className="text-[11px] font-semibold uppercase tracking-wide text-[#64748B]">
              {t('subscriptions.admin.revenueSplit.manualTransferAudit.filters.status', {
                defaultValue: 'Status'
              })}
            </span>
            <select
              className="h-10 rounded-lg border border-[#CBD5E1] px-3 text-[13px] text-[#334155] bg-white"
              value={status}
              onChange={(e) => setStatus(e.target.value)}
            >
              <option value="">
                {t('subscriptions.admin.revenueSplit.filters.all', { defaultValue: 'All' })}
              </option>
              <option value="pending">
                {t('subscriptions.admin.revenueSplit.manualTransferAudit.status.pending', {
                  defaultValue: 'Pending'
                })}
              </option>
              <option value="reconciled">
                {t('subscriptions.admin.revenueSplit.manualTransferAudit.status.reconciled', {
                  defaultValue: 'Reconciled'
                })}
              </option>
            </select>
          </label>

          <button
            type="button"
            className="h-10 px-4 rounded-lg bg-[#0F172A] text-white text-[13px] font-semibold hover:bg-[#1E293B] disabled:opacity-50 min-w-[130px] whitespace-nowrap"
            onClick={downloadAuditCsv}
            disabled={downloading || loading}
          >
            {downloading
              ? t('subscriptions.admin.revenueSplit.filters.downloading', {
                defaultValue: 'Downloading...'
              })
              : t('subscriptions.admin.revenueSplit.filters.exportCsv', {
                defaultValue: 'Export CSV'
              })}
          </button>
        </div>

        {/* Search */}
        <div className="mt-3">
          <input
            type="text"
            placeholder={t(
              'subscriptions.admin.revenueSplit.manualTransferAudit.filters.searchPlaceholder',
              { defaultValue: 'Search by team name...' }
            )}
            className="h-10 w-full max-w-xs rounded-lg border border-[#CBD5E1] px-3 text-[13px] text-[#334155] bg-white"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />
        </div>
      </div>

      {/* Totals */}
      {!loading && totals.length > 0 && (
        <div className="px-6 py-4 bg-white border-b border-[#E2E8F0] shrink-0">
          <div className="flex flex-wrap gap-3">
            {totals.map((entry) => {
              const total_amount = entry.totalDue + entry.totalReconciled;
              const reconciled_pct = total_amount > 0 ? Math.round((entry.totalReconciled / total_amount) * 100) : 0;
              return (
                <div key={entry.currency} className="flex flex-col gap-2 rounded-xl border border-[#E2E8F0] bg-[#F8FAFC] px-4 py-3 min-w-[180px]">
                  <div className="flex items-center justify-between">
                    <span className="text-[13px] font-bold text-[#0F172A]">{entry.currency}</span>
                    <span className="text-[11px] font-medium text-[#64748B]">{reconciled_pct}% done</span>
                  </div>
                  <div className="w-full h-1.5 rounded-full bg-[#E2E8F0] overflow-hidden">
                    <div
                      className="h-full rounded-full bg-emerald-500 transition-all"
                      style={{ width: `${reconciled_pct}%` }}
                    />
                  </div>
                  <div className="flex justify-between text-[12px]">
                    <span className="text-amber-700">
                      <span className="text-[10px] uppercase tracking-wide font-semibold block text-[#94A3B8]">
                        {t('subscriptions.admin.revenueSplit.manualTransferAudit.totals.outstanding', { defaultValue: 'Outstanding' })}
                      </span>
                      <span className="font-semibold">{formatCurrencyAmount(entry.totalDue, entry.currency)}</span>
                    </span>
                    <span className="text-emerald-700 text-right">
                      <span className="text-[10px] uppercase tracking-wide font-semibold block text-[#94A3B8]">
                        {t('subscriptions.admin.revenueSplit.manualTransferAudit.totals.reconciled', { defaultValue: 'Reconciled' })}
                      </span>
                      <span className="font-semibold">{formatCurrencyAmount(entry.totalReconciled, entry.currency)}</span>
                    </span>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* Table */}
      <div className="overflow-y-auto flex-1">
        {loading && (
          <Col wid="100%" marg="80px 0" centerAll noFlex>
            <Spinner2 />
          </Col>
        )}

        {!loading && error && (
          <Col wid="100%" marg="80px 0" centerAll noFlex>
            <Text className="text-[13px] text-red-600">
              {t('subscriptions.admin.revenueSplit.error', {
                defaultValue: 'Failed to load revenue split report.'
              })}
            </Text>
          </Col>
        )}

        {!loading && !error && rows.length === 0 && (
          <Col wid="100%" marg="80px 0" centerAll noFlex>
            <Text className="text-[13px] text-[#64748B]">
              {t('subscriptions.admin.revenueSplit.empty', {
                defaultValue: 'No data found for the selected filters.'
              })}
            </Text>
          </Col>
        )}

        {!loading && !error && rows.length > 0 && (
          <div className="p-6 space-y-4">
            {selectedEventIds.size > 0 && (
              <div className="flex items-center gap-3 px-4 py-2.5 bg-blue-50 border border-blue-200 rounded-xl text-[13px]">
                <span className="text-blue-700 font-semibold">
                  {selectedEventIds.size} row{selectedEventIds.size > 1 ? 's' : ''} selected
                </span>
                <button
                  type="button"
                  className="px-3 py-1.5 rounded-lg bg-emerald-600 text-white text-[12px] font-semibold hover:bg-emerald-700"
                  onClick={() => { setBulkReconcileNote(''); setBulkReconcileOpen(true); }}
                >
                  Reconcile Selected ({selectedEventIds.size})
                </button>
                <button
                  type="button"
                  className="px-3 py-1.5 rounded-lg border border-[#CBD5E1] text-[12px] text-[#334155] hover:bg-white"
                  onClick={() => setSelectedEventIds(new Set())}
                >
                  Clear selection
                </button>
              </div>
            )}

            <div className="rounded-xl border border-[#E2E8F0] bg-white overflow-hidden">
              <div className="overflow-auto">
                <table className="min-w-[1440px] w-full border-separate border-spacing-0">
                  <thead className="sticky top-0 z-10 bg-white">
                    <tr>
                      <th className="px-3 py-3 border-b border-[#E2E8F0] bg-white w-10 text-center">
                        <input
                          type="checkbox"
                          className="w-4 h-4 rounded border-[#CBD5E1] accent-[#0F172A] cursor-pointer"
                          checked={allPendingSelected}
                          ref={(el) => { if (el) el.indeterminate = somePendingSelected; }}
                          onChange={(e) => {
                            if (e.target.checked) {
                              setSelectedEventIds(new Set(pendingRows.map((r) => r.eventId)));
                            } else {
                              setSelectedEventIds(new Set());
                            }
                          }}
                          disabled={pendingRows.length === 0}
                        />
                      </th>
                      {[
                        'team',
                        'occurredAt',
                        'interval',
                        'currency',
                        'amount',
                        'manualDue',
                        'status',
                        'reconciledBy',
                        'reconciledAt',
                        'note',
                        'skipReason',
                        'actions'
                      ].map((col) => (
                        <th
                          key={col}
                          className="px-3 py-3 text-[11px] font-semibold uppercase tracking-wide text-[#64748B] border-b border-[#E2E8F0] bg-white text-center first:text-left first:pl-5"
                        >
                          {col === 'team'
                            ? t('subscriptions.admin.revenueSplit.table.team', {
                              defaultValue: 'Team'
                            })
                            : col === 'occurredAt'
                              ? t(
                                'subscriptions.admin.revenueSplit.manualTransferAudit.table.occurredAt',
                                { defaultValue: 'Date' }
                              )
                              : col === 'interval'
                                ? t(
                                  'subscriptions.admin.revenueSplit.manualTransferAudit.table.interval',
                                  { defaultValue: 'Interval' }
                                )
                                : col === 'currency'
                                  ? t('subscriptions.admin.revenueSplit.table.currency', {
                                    defaultValue: 'Currency'
                                  })
                                  : col === 'amount'
                                    ? t('subscriptions.admin.revenueSplit.table.grossRevenue', {
                                      defaultValue: 'Amount'
                                    })
                                    : col === 'manualDue'
                                      ? t(
                                        'subscriptions.admin.revenueSplit.manualTransferAudit.table.manualDue',
                                        { defaultValue: 'Manual Due' }
                                      )
                                      : col === 'status'
                                        ? t(
                                          'subscriptions.admin.revenueSplit.manualTransferAudit.filters.status',
                                          { defaultValue: 'Status' }
                                        )
                                        : col === 'reconciledBy'
                                          ? t(
                                            'subscriptions.admin.revenueSplit.manualTransferAudit.table.reconciledBy',
                                            { defaultValue: 'Reconciled By' }
                                          )
                                          : col === 'reconciledAt'
                                            ? t(
                                              'subscriptions.admin.revenueSplit.manualTransferAudit.table.reconciledAt',
                                              { defaultValue: 'Reconciled At' }
                                            )
                                            : col === 'note'
                                              ? t(
                                                'subscriptions.admin.revenueSplit.manualTransferAudit.table.note',
                                                { defaultValue: 'Note' }
                                              )
                                              : col === 'skipReason'
                                                ? t(
                                                  'subscriptions.admin.revenueSplit.manualTransferAudit.table.skipReason',
                                                  { defaultValue: 'Skip Reason' }
                                                )
                                                : t(
                                                  'subscriptions.admin.revenueSplit.manualTransferAudit.table.actions',
                                                  { defaultValue: 'Actions' }
                                                )}
                        </th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {rows.map((row) => (
                      <tr key={row.eventId} className={`hover:bg-[#F8FAFC] ${selectedEventIds.has(row.eventId) ? 'bg-blue-50' : ''}`}>
                        <td className="px-3 py-3 border-b border-[#E2E8F0] text-center w-10">
                          {row.manualTransferStatus === 'pending' && (
                            <input
                              type="checkbox"
                              className="w-4 h-4 rounded border-[#CBD5E1] accent-[#0F172A] cursor-pointer"
                              checked={selectedEventIds.has(row.eventId)}
                              onChange={(e) => {
                                setSelectedEventIds((prev) => {
                                  const next = new Set(prev);
                                  if (e.target.checked) next.add(row.eventId);
                                  else next.delete(row.eventId);
                                  return next;
                                });
                              }}
                            />
                          )}
                        </td>
                        <td className="px-3 py-3 border-b border-[#E2E8F0] pl-5 text-left">
                          <div className="text-[13px] font-semibold text-[#0F172A]">
                            {row.teamName}
                          </div>
                          {row.countryCode && (
                            <div className="text-[11px] text-[#64748B] mt-0.5">
                              {row.countryCode}
                            </div>
                          )}
                        </td>
                        <td className="px-3 py-3 border-b border-[#E2E8F0] text-center text-[12px] text-[#334155]">
                          {new Date(row.occurredAt).toLocaleDateString()}
                        </td>
                        <td className="px-3 py-3 border-b border-[#E2E8F0] text-center text-[12px] text-[#334155] capitalize">
                          {row.interval || '-'}
                        </td>
                        <td className="px-3 py-3 border-b border-[#E2E8F0] text-center text-[13px] font-semibold text-[#334155]">
                          {row.currency}
                        </td>
                        <td className="px-3 py-3 border-b border-[#E2E8F0] text-center text-[13px] text-[#0F172A]">
                          {formatCurrencyAmount(row.amount, row.currency)}
                        </td>
                        <td className="px-3 py-3 border-b border-[#E2E8F0] text-center text-[13px] font-semibold text-amber-700">
                          {formatCurrencyAmount(row.manualTransferDueAmount, row.currency)}
                        </td>
                        <td className="px-3 py-3 border-b border-[#E2E8F0] text-center">
                          <ManualTransferStatusBadge status={row.manualTransferStatus} t={t} />
                        </td>
                        <td className="px-3 py-3 border-b border-[#E2E8F0] text-center">
                          {row.manualTransferStatus === 'reconciled' && row.reconciledBy ? (
                            <span className="text-[12px] text-[#334155] break-all">
                              {row.reconciledBy}
                            </span>
                          ) : (
                            <span className="text-[12px] text-[#94A3B8]">-</span>
                          )}
                        </td>
                        <td className="px-3 py-3 border-b border-[#E2E8F0] text-center text-[12px] text-[#334155]">
                          {row.reconciledAt ? new Date(row.reconciledAt).toLocaleDateString() : '-'}
                        </td>
                        <td className="px-3 py-3 border-b border-[#E2E8F0] text-center">
                          {row.note ? (
                            <span
                              className="text-[12px] text-[#334155] italic max-w-[180px] inline-block truncate align-middle"
                              title={row.note}
                            >
                              {row.note}
                            </span>
                          ) : (
                            <span className="text-[12px] text-[#94A3B8]">-</span>
                          )}
                        </td>
                        <td className="px-3 py-3 border-b border-[#E2E8F0] text-center">
                          <SkipReasonBadge reason={row.splitSkippedReason} t={t} />
                        </td>
                        <td className="px-3 py-3 border-b border-[#E2E8F0] text-center">
                          {row.manualTransferStatus === 'pending' && (
                            <button
                              type="button"
                              className="px-3 py-1.5 rounded-lg bg-emerald-600 text-white text-[12px] font-semibold hover:bg-emerald-700 disabled:opacity-50"
                              onClick={() => openReconcileModal(row)}
                            >
                              {t(
                                'subscriptions.admin.revenueSplit.manualTransferAudit.reconcile.action',
                                { defaultValue: 'Reconcile' }
                              )}
                            </button>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>

            {/* Pagination */}
            {total > 0 && (
              <div className="flex flex-wrap items-center justify-between gap-3 text-[13px] text-[#334155]">
                <div className="flex items-center gap-3">
                  <div className="flex items-center gap-2">
                    <span className="text-[12px] text-[#64748B]">Rows per page:</span>
                    <select
                      value={pageSize}
                      onChange={(e) => setPageSize(Number(e.target.value))}
                      className="h-8 rounded-lg border border-[#CBD5E1] px-2 text-[12px] text-[#334155] bg-white"
                    >
                      {PAGE_SIZE_OPTIONS.map((s) => (
                        <option key={s} value={s}>{s}</option>
                      ))}
                    </select>
                  </div>
                  <span className="text-[12px] text-[#64748B]">
                    {t('subscriptions.admin.revenueSplit.manualTransferAudit.pagination.showing', {
                      defaultValue: 'Showing {{from}}–{{to}} of {{total}}',
                      from: (page - 1) * pageSize + 1,
                      to: Math.min(page * pageSize, total),
                      total
                    })}
                  </span>
                </div>
                {totalPages > 1 && (
                  <div className="flex items-center gap-1">
                    <button
                      type="button"
                      className={`min-w-[32px] h-[32px] px-2 rounded-lg border text-[12px] font-medium transition-colors ${page <= 1 ? 'border-[#CBD5E1] text-[#475569] opacity-40 cursor-not-allowed' : 'border-[#CBD5E1] text-[#475569] hover:bg-[#F1F5F9]'}`}
                      onClick={() => setPage((p) => p - 1)}
                      disabled={page <= 1}
                    >
                      ←
                    </button>
                    {getPageNumbers(page, totalPages).map((p, i) =>
                      p === '...' ? (
                        <span key={`ellipsis-${i}`} className="px-1 text-[12px] text-[#94A3B8] select-none">…</span>
                      ) : (
                        <button
                          key={p}
                          type="button"
                          className={`min-w-[32px] h-[32px] px-2 rounded-lg border text-[12px] font-medium transition-colors ${p === page ? 'border-[#334155] bg-[#334155] text-white' : 'border-[#CBD5E1] text-[#475569] hover:bg-[#F1F5F9]'}`}
                          onClick={() => p !== page && setPage(p)}
                        >
                          {p}
                        </button>
                      )
                    )}
                    <button
                      type="button"
                      className={`min-w-[32px] h-[32px] px-2 rounded-lg border text-[12px] font-medium transition-colors ${page >= totalPages ? 'border-[#CBD5E1] text-[#475569] opacity-40 cursor-not-allowed' : 'border-[#CBD5E1] text-[#475569] hover:bg-[#F1F5F9]'}`}
                      onClick={() => setPage((p) => p + 1)}
                      disabled={page >= totalPages}
                    >
                      →
                    </button>
                  </div>
                )}
              </div>
            )}
          </div>
        )}
      </div>

      {/* Bulk reconcile modal */}
      {bulkReconcileOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40">
          <div className="bg-white rounded-xl shadow-xl p-6 w-full max-w-md mx-4">
            <h2 className="text-[16px] font-semibold text-[#0F172A] mb-1">
              {t('subscriptions.admin.revenueSplit.manualTransferAudit.reconcile.action', {
                defaultValue: 'Reconcile'
              })}
            </h2>
            <p className="text-[13px] text-[#64748B] mb-4">
              {t('subscriptions.admin.revenueSplit.manualTransferAudit.reconcile.bulkDescription', {
                defaultValue: 'Reconcile {{count}} selected pending items',
                count: selectedEventIds.size
              })}
            </p>

            <label className="flex flex-col gap-1 mb-4">
              <span className="text-[11px] font-semibold uppercase tracking-wide text-[#64748B]">
                {t('subscriptions.admin.revenueSplit.manualTransferAudit.reconcile.note', {
                  defaultValue: 'Note (optional)'
                })}
              </span>
              <textarea
                className="rounded-lg border border-[#CBD5E1] px-3 py-2 text-[13px] text-[#334155] resize-none h-20"
                value={bulkReconcileNote}
                onChange={(e) => setBulkReconcileNote(e.target.value)}
                placeholder={t(
                  'subscriptions.admin.revenueSplit.manualTransferAudit.reconcile.notePlaceholder',
                  { defaultValue: 'e.g. paid via bank transfer on 2024-03-01' }
                )}
              />
            </label>

            <div className="flex gap-3 justify-end">
              <button
                type="button"
                className="px-4 py-2 rounded-lg border border-[#CBD5E1] text-[13px] text-[#334155] hover:bg-[#F8FAFC]"
                onClick={() => setBulkReconcileOpen(false)}
                disabled={bulkReconciling}
              >
                {t('subscriptions.admin.revenueSplit.manualTransferAudit.reconcile.cancel', {
                  defaultValue: 'Cancel'
                })}
              </button>
              <button
                type="button"
                className="px-4 py-2 rounded-lg bg-[#0F172A] text-white text-[13px] font-semibold hover:bg-[#1E293B] disabled:opacity-50"
                onClick={submitBulkReconcile}
                disabled={bulkReconciling}
              >
                {bulkReconciling
                  ? '...'
                  : t('subscriptions.admin.revenueSplit.manualTransferAudit.reconcile.confirm', {
                    defaultValue: 'Confirm'
                  })}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Reconcile modal */}
      {reconcileModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40">
          <div className="bg-white rounded-xl shadow-xl p-6 w-full max-w-md mx-4">
            <h2 className="text-[16px] font-semibold text-[#0F172A] mb-1">
              {t('subscriptions.admin.revenueSplit.manualTransferAudit.reconcile.action', {
                defaultValue: 'Reconcile'
              })}
            </h2>
            <p className="text-[13px] text-[#64748B] mb-4">
              {reconcileModal.row.teamName} ·{' '}
              {formatCurrencyAmount(
                reconcileModal.row.manualTransferDueAmount,
                reconcileModal.row.currency
              )}
            </p>

            <label className="flex flex-col gap-1 mb-4">
              <span className="text-[11px] font-semibold uppercase tracking-wide text-[#64748B]">
                {t('subscriptions.admin.revenueSplit.manualTransferAudit.reconcile.note', {
                  defaultValue: 'Note (optional)'
                })}
              </span>
              <textarea
                className="rounded-lg border border-[#CBD5E1] px-3 py-2 text-[13px] text-[#334155] resize-none h-20"
                value={reconcileNote}
                onChange={(e) => setReconcileNote(e.target.value)}
                placeholder={t(
                  'subscriptions.admin.revenueSplit.manualTransferAudit.reconcile.notePlaceholder',
                  { defaultValue: 'e.g. paid via bank transfer on 2024-03-01' }
                )}
              />
            </label>

            <div className="flex gap-3 justify-end">
              <button
                type="button"
                className="px-4 py-2 rounded-lg border border-[#CBD5E1] text-[13px] text-[#334155] hover:bg-[#F8FAFC]"
                onClick={() => setReconcileModal(null)}
                disabled={reconciling}
              >
                {t('subscriptions.admin.revenueSplit.manualTransferAudit.reconcile.cancel', {
                  defaultValue: 'Cancel'
                })}
              </button>
              <button
                type="button"
                className="px-4 py-2 rounded-lg bg-[#0F172A] text-white text-[13px] font-semibold hover:bg-[#1E293B] disabled:opacity-50"
                onClick={submitReconcile}
                disabled={reconciling}
              >
                {reconciling
                  ? '...'
                  : t('subscriptions.admin.revenueSplit.manualTransferAudit.reconcile.confirm', {
                    defaultValue: 'Confirm'
                  })}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ManualAuditTab;